/**
 * React Query Provider
 * Configures global query settings for data fetching
 */

import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Create a client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,      // 5 min - show cached data instantly, refetch only when stale
      gcTime: 30 * 60 * 1000,        // 30 minutes - keep in cache (formerly cacheTime)
      retry: 2,                       // Retry failed requests twice
      refetchOnWindowFocus: false,    // Don't refetch on browser tab focus (useFocusEffect handles navigation focus)
      refetchOnReconnect: true,       // Refetch when network reconnects
      refetchOnMount: true,           // Refetch on mount only if data is stale
    },
    mutations: {
      retry: 1,                       // Retry failed mutations once
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => (
  <QueryClientProvider client={queryClient}>
    {children}
  </QueryClientProvider>
);

// Export queryClient for manual cache manipulation
export { queryClient };

// Query key factory for consistent key generation
export const queryKeys = {
  // Dashboard
  dashboard: (userId?: number) => ['dashboard', userId] as const,

  // Goals
  goals: {
    all: ['goals'] as const,
    list: (filters?: Record<string, any>) => ['goals', 'list', filters] as const,
    detail: (id: number) => ['goals', 'detail', id] as const,
    kpis: (goalId: number) => ['goals', goalId, 'kpis'] as const,
  },

  // Initiatives
  initiatives: {
    all: ['initiatives'] as const,
    list: (filters?: Record<string, any>) => ['initiatives', 'list', filters] as const,
    detail: (id: number) => ['initiatives', 'detail', id] as const,
    kpis: (initiativeId: number) => ['initiatives', initiativeId, 'kpis'] as const,
  },

  // KPIs
  kpis: {
    all: ['kpis'] as const,
    list: (filters?: Record<string, any>) => ['kpis', 'list', filters] as const,
    detail: (id: number) => ['kpis', 'detail', id] as const,
    readings: (kpiId: number) => ['kpis', kpiId, 'readings'] as const,
  },

  // Organizations
  organizations: {
    all: ['organizations'] as const,
    list: () => ['organizations', 'list'] as const,
    detail: (id: number) => ['organizations', 'detail', id] as const,
  },

  // Notifications
  notifications: (userId?: number) => ['notifications', userId] as const,

  // Schedules
  schedules: (filters?: Record<string, any>) => ['schedules', filters] as const,

  // Processes
  processes: {
    all: ['processes'] as const,
    list: (filters?: Record<string, any>) => ['processes', 'list', filters] as const,
    detail: (id: number) => ['processes', 'detail', id] as const,
  },

  // Services
  services: {
    all: ['services'] as const,
    list: (filters?: Record<string, any>) => ['services', 'list', filters] as const,
    detail: (id: number) => ['services', 'detail', id] as const,
  },

  // Unit Goals
  unitGoals: {
    all: ['unitGoals'] as const,
    list: (filters?: Record<string, any>) => ['unitGoals', 'list', filters] as const,
    detail: (id: number) => ['unitGoals', 'detail', id] as const,
  },

  // Employees
  employees: {
    all: ['employees'] as const,
    list: (filters?: Record<string, any>) => ['employees', 'list', filters] as const,
    detail: (id: number) => ['employees', 'detail', id] as const,
  },

  // Risks
  risks: {
    all: ['risks'] as const,
    list: (filters?: Record<string, any>) => ['risks', 'list', filters] as const,
    detail: (id: number) => ['risks', 'detail', id] as const,
  },

  // Organizational Units (for dropdowns)
  organizationalUnits: {
    all: ['organizationalUnits'] as const,
    list: (filters?: Record<string, any>) => ['organizationalUnits', 'list', filters] as const,
  },

  // User
  user: (userId: number) => ['user', userId] as const,
};

// Stale times for different data types
// Data shows instantly from cache; background refetch happens only when stale
export const STALE_TIMES = {
  DASHBOARD: 5 * 60 * 1000,       // 5 minutes
  GOALS: 5 * 60 * 1000,           // 5 minutes
  INITIATIVES: 5 * 60 * 1000,     // 5 minutes
  KPIS: 5 * 60 * 1000,            // 5 minutes
  ORGANIZATIONS: 10 * 60 * 1000,  // 10 minutes (rarely changes)
  NOTIFICATIONS: 30 * 1000,       // 30 seconds (real-time data)
  USER: 30 * 60 * 1000,           // 30 minutes (profile rarely changes)
  SCHEDULES: 5 * 60 * 1000,       // 5 minutes
} as const;

export default QueryProvider;
